import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { fetchEventBySlug } from "@/lib/api/events";
import { requestOtp, AuthError } from "@/lib/auth";
import { useAuthUser } from "@/lib/use-auth-user";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Field } from "@/components/ui/Field";
import { Input } from "@/components/ui/Input";
import { Loader, ErrorBlock } from "@/components/ui/States";
import { MobileFrame } from "@/components/ui/MobileFrame";
import { TelegramLoginButton } from "@/components/auth/TelegramLoginButton";

// Cache the partially-filled email + consents per slug so the user can hop
// out to /privacy or /terms and come back without retyping. sessionStorage
// (not localStorage) so it goes away when the tab closes — these are not
// long-term commitments and we don't want stale checked boxes on a fresh
// visit days later.
const joinStateKey = (slug: string) => `finally.meetup.join.${slug}`;

interface JoinState {
  email: string;
  privacy: boolean;
  terms: boolean;
}

function readJoinState(slug: string | undefined): JoinState {
  if (!slug) return { email: "", privacy: false, terms: false };
  try {
    const raw = sessionStorage.getItem(joinStateKey(slug));
    if (!raw) return { email: "", privacy: false, terms: false };
    const parsed = JSON.parse(raw) as Partial<JoinState>;
    return {
      email: typeof parsed.email === "string" ? parsed.email : "",
      privacy: Boolean(parsed.privacy),
      terms: Boolean(parsed.terms),
    };
  } catch {
    return { email: "", privacy: false, terms: false };
  }
}

export function JoinEmailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuthUser();

  const initial = readJoinState(slug);
  const [email, setEmail] = useState(initial.email);
  const [privacy, setPrivacy] = useState(initial.privacy);
  const [terms, setTerms] = useState(initial.terms);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If a Supabase Auth session already exists (e.g. logged in with
  // Telegram or earlier via email-OTP), don't ask the user to authenticate
  // a second time. Bounce them up to the welcome page for this slug —
  // its routing logic decides between /continue (need to finish profile),
  // /profile (need to record pitch), and /feed (fully set up).
  useEffect(() => {
    if (authLoading) return;
    if (user && slug) {
      navigate(`/e/${slug}`, { replace: true });
    }
  }, [user, authLoading, slug, navigate]);

  // Persist partial state on every change. Cheap — JSON.stringify of three
  // primitives is negligible. Survives the round-trip to /privacy + /terms.
  useEffect(() => {
    if (!slug) return;
    try {
      sessionStorage.setItem(
        joinStateKey(slug),
        JSON.stringify({ email, privacy, terms } satisfies JoinState),
      );
    } catch {
      /* storage full / disabled — non-critical */
    }
  }, [slug, email, privacy, terms]);

  const { data: event, isLoading } = useQuery({
    queryKey: ["event-slug", slug],
    queryFn: () => fetchEventBySlug(slug ?? ""),
    enabled: Boolean(slug),
  });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slug) return;
    setSubmitting(true);
    setError(null);
    try {
      const normalized = email.trim().toLowerCase();
      await requestOtp(normalized);
      // Clear the cached draft now that the code is on the way — the verify
      // page is the next step and shouldn't be bypassed by a stale cache.
      try {
        sessionStorage.removeItem(joinStateKey(slug));
      } catch {
        /* ignore */
      }
      navigate(`/e/${slug}/verify?email=${encodeURIComponent(normalized)}`);
    } catch (err) {
      setError(err instanceof AuthError ? err.message : "Couldn't send the code. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <MobileFrame>
        <Loader label="Loading event..." />
      </MobileFrame>
    );
  }

  if (!event) {
    return (
      <MobileFrame>
        <Card padding="lg" className="m-6 text-center">
          <p className="text-[14px] text-[#6c757d]">Event not found.</p>
        </Card>
      </MobileFrame>
    );
  }

  return (
    <MobileFrame>
      <form onSubmit={onSubmit} className="flex-1 px-4 py-6 animate-fade-in" autoComplete="off">
        <h1 className="text-[24px] font-bold tracking-tight text-[#1a1a1a]">Join</h1>
        <p className="mt-2 text-[14px] text-[#6c757d]">
          Joining <span className="font-semibold text-[#1a1a1a]">{event.title}</span>
        </p>

        {error ? <ErrorBlock title="Error" message={error} className="mt-5" /> : null}

        <div className="mt-6 flex flex-col gap-4">
          <Field label="Email" required>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="alex@company.com"
              autoComplete="email"
              required
            />
          </Field>

          <div className="flex flex-col gap-3 rounded-[10px] bg-[#f8fafc] p-4">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={privacy}
                onChange={(e) => setPrivacy(e.target.checked)}
                className="mt-1 h-4 w-4 accent-[#2563eb]"
              />
              <span className="text-[13px] text-[#475569]">
                I agree to the{" "}
                <Link to="/privacy" className="font-semibold text-[#2563eb]">
                  Privacy Policy
                </Link>{" "}
                and consent to my data being processed for matching.
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={terms}
                onChange={(e) => setTerms(e.target.checked)}
                className="mt-1 h-4 w-4 accent-[#2563eb]"
              />
              <span className="text-[13px] text-[#475569]">
                I agree to the{" "}
                <Link to="/terms" className="font-semibold text-[#2563eb]">
                  Terms of Service
                </Link>
                .
              </span>
            </label>
          </div>

          <Button
            type="submit"
            loading={submitting}
            disabled={!privacy || !terms || !email.trim()}
            fullWidth
          >
            {submitting ? "Sending code..." : "Send code"}
          </Button>

          <p className="mt-2 text-center text-[13px] text-[#6c757d]">
            We'll send a 6-digit code to your email — no password needed.
          </p>

          {/* Telegram as an alternative auth path. The widget needs the
              participant to have agreed to privacy + terms before they
              tap it — same gate as the email submit — otherwise we'd
              be smuggling consent past the checkboxes. The button is
              hidden until both boxes are ticked. */}
          {privacy && terms ? (
            <>
              <div className="my-5 flex items-center gap-3 text-[12px] text-[#94a3b8]">
                <span className="flex-1 h-px bg-[#e2e8f0]" />
                or
                <span className="flex-1 h-px bg-[#e2e8f0]" />
              </div>
              <div className="flex flex-col items-center gap-2">
                <TelegramLoginButton />
                <p className="text-center text-[12px] text-[#6c757d]">
                  Continue with Telegram — your bot connection is set up
                  in the same step.
                </p>
              </div>
            </>
          ) : null}
        </div>
      </form>
    </MobileFrame>
  );
}
